import React, { useEffect, useRef, useState } from "react";
import { ICON_BASE_PATH } from "../../../../utils/config";
import {
  TextBox,
  type TextBoxChangeEvent,
  type TextBoxHandle,
} from "@progress/kendo-react-inputs";
import { t } from "i18next";

interface UniInputProps {
  fieldName?: string;
  label?: string;
  value: string;
  onChange: (value: string) => void;
  onValidate?: (field: string, isValid: boolean) => void;
  validationMessage?: string;
  triggerValidation?: boolean;
  starlable?: boolean;
  disable?: boolean;
  max?: number;
  minLength?: number;
  /** Icon filename e.g. "org2.svg" — resolved against ICON_BASE_PATH */
  iconFile?: string;
  /** Tooltip text — if omitted the ⓘ icon is not rendered at all */
  tooltip?: string;
  /** Direction the tooltip floats relative to the ⓘ icon. Defaults to "up" */
  tooltipFloat?: "up" | "down";
  /** Input type passed through to the underlying Kendo TextBox (e.g. "text", "password"). Defaults to "text" */
  type?: string;
}

const UniInput: React.FC<UniInputProps> = ({
  fieldName = "uniInput",
  label = "Label",
  value,
  onChange,
  onValidate,
  validationMessage,
  triggerValidation = false,
  starlable = true,
  disable = false,
  max = 50,
  minLength = 5,
  iconFile,
  tooltip,
  tooltipFloat = "up",
  type = "text",
}) => {
  const inputRef = useRef<TextBoxHandle>(null);
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  const isPassword = type === "password";

  const hasValue = !!value?.trim();

  // Sanitize input — allow alphanumeric, dash, underscore, and dot.
  // FIX: the previous regex [^A-Za-z0-9-_] stripped "." entirely, which
  // made it impossible to type values like Client IDs that require a
  // literal "." (e.g. "1000.0V5M2IXVLFFSJ6XFC1A3SVAEX21Q4A").
  const sanitize = (s: string) => s.replace(/[^A-Za-z0-9._-]/g, "");

  const validate = (val: string): string => {
    if (!val.trim()) return `${t("please_enter_the")} ${label}.`;
    if (val.trim().length < minLength)
      return `${label} ${t("must_be_at_least")} ${minLength} ${t("characters")}.`;
    if (val.trim().length > max)
      return `${label} ${t("cannot_exceed")} ${max} ${t("characters")}.`;
    return "";
  };

  const updateValidation = (val: string) => {
    const newError = validate(val);
    setError(newError);
    onValidate?.(fieldName, !newError);
    return newError;
  };

  const handleChange = (e: TextBoxChangeEvent) => {
    const raw = String(e.value ?? "");
    let filtered = sanitize(raw);
    if (max && filtered.length > max) filtered = filtered.slice(0, max);
    onChange(filtered);

    // FIX: do NOT clear the error while the PARENT is driving the message.
    // Previously any keystroke that satisfied the local rules (minLength etc.)
    // called setError(""), wiping a parent error such as the sk_live_/sk_test_
    // prefix rule — and because `validationMessage` was the same string, the
    // useEffect below never re-fired to restore it. That's why the message
    // flashed once and then vanished for input like "e9r9ew809r8w".
    if (touched && error && !validationMessage) {
      const newError = validate(filtered);
      if (!newError) setError("");
    }
  };

  const handleBlur = () => {
    setTouched(true);
    updateValidation(value);
  };

  const toggleVisibility = () => setIsVisible((prev) => !prev);

  useEffect(() => {
    if (triggerValidation) {
      setTouched(true);
      updateValidation(value);
    }
  }, [triggerValidation]);

  useEffect(() => {
    if (validationMessage) {
      setError(validationMessage);
      setTouched(true);
      onValidate?.(fieldName, false);
    } else if (touched) {
      updateValidation(value);
    }
  }, [validationMessage]);

  // FIX: the parent message always wins and is shown immediately (no need to
  // blur first). Local validation still applies when there is no parent message.
  const effectiveError = validationMessage || error;
  const isInvalid = !!effectiveError && (!!validationMessage || touched);

  const InfoIcon = tooltip ? (
    <div
      className="gx_info_icon_wrapper"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <img src={`${ICON_BASE_PATH}infoS.svg`} alt="Identify Icon 1" />
      {showTooltip && (
        <div
          className={`gx-uni-tooltip ${
            tooltipFloat === "down" ? "gx_tooltip_down" : "gx_tooltip_up"
          }`}
        >
          <p className="gx-p12-r">{tooltip}</p>

          {tooltipFloat === "down" ? (
            <img
              src={`${ICON_BASE_PATH}tooltiptriangle.svg`}
              alt="Tooltip arrow up"
              className="tooltip-arrow"
            />
          ) : (
            <img
              src={`${ICON_BASE_PATH}tooltiptriangle.svg`}
              alt="Tooltip arrow down"
              className="tooltip-arrow"
            />
          )}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="gx_input_container">
      <div
        className={`input-label gx-font-semibold flex ${hasValue ? "floating" : ""}`}
      >
        <label className="gx-font-semibold">
          {t(label)}
          {starlable && (
            <span
              className={`gx_input_star ${
                hasValue ? "gx_input_star_float" : "gx_input_star_normal"
              }`}
            >
              *
            </span>
          )}
        </label>

        {hasValue && InfoIcon}
      </div>

      {/* Input row */}
      <div className={`gx_input_cont ${isInvalid ? "invalid" : "valid"}`}>
        {/* Leading icon */}
        {iconFile && (
          <img src={`${ICON_BASE_PATH}${iconFile}`} alt={fieldName} />
        )}

        <TextBox
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          type={isPassword ? (isVisible ? "text" : "password") : type}
          autoComplete="off"
          max={max}
          disabled={disable}
          className="gx-textbox-css"
        />

        {isPassword && (
          <img
            src={`${ICON_BASE_PATH}${isVisible ? "eyeopen.svg" : "eyeclose.svg"}`}
            alt={isVisible ? "Hide" : "Show"}
            onClick={toggleVisibility}
            className="cursor-pointer invite-code-icon"
            aria-label={isVisible ? "Hide password" : "Show password"}
          />
        )}

        {/* ⓘ shown inside the input row when the label is NOT yet floated */}
        {!hasValue && InfoIcon}
      </div>

      {isInvalid && (
        <div className="gx_input_validationmessage gx-font-regular">
          {effectiveError}
        </div>
      )}
    </div>
  );
};

export default UniInput;
